import { Mark, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { ySyncPluginKey } from "y-prosemirror";

const Annotation = Mark.create({
  name: "annotation",
  excludes: "",
  priority: 1000,

  /**
   * Configuration options for the annotation mark.
   * @returns {Object}
   */
  addOptions() {
    return {
      HTMLAttributes: {},
      commentsManager: null,
      onHover: () => {},
      onLeave: () => {},
      onClick: () => {},
      /** Called when an annotation mark disappears from the document (e.g. text edit). */
      onMarkRemoved: null,
      preserveCommentsOnUndoRedo: false,
    };
  },

  addCommands() {
    return {
      setAnnotation: (attributes) => {
        return ({ commands, _tr, _state }) => {
          return commands.setMark(this.name, attributes);
        };
      },
      unsetAnnotation:
        (annotationId) =>
        ({ tr, state }) => {
          let hasChanged = false;

          // Instead of using selection, scan the whole document
          state.doc.descendants((node, pos) => {
            const marks = node.marks.filter((m) => m.type === this.type);

            marks.forEach((mark) => {
              const ids = mark.attrs?.annotationId?.split(" ") ?? [];
              if (ids.includes(annotationId)) {
                // Remove this specific mark
                tr.removeMark(pos, pos + node.nodeSize, this.type);

                // If there were multiple ids, add back mark with remaining ids
                const remainingIds = ids.filter((id) => id !== annotationId);
                if (remainingIds.length > 0) {
                  tr.addMark(pos, pos + node.nodeSize, this.type.create({ annotationId: remainingIds.join(" ") }));
                }

                hasChanged = true;
              }
            });
          });

          return hasChanged;
        },
      unsetAllAnnotations:
        () =>
        ({ tr, state }) => {
          tr.removeMark(0, state.doc.content.size, this.type);
          return true;
        },
    };
  },

  addAttributes() {
    return {
      annotationId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-annotation-id"),
        renderHTML: (attributes) => {
          if (!attributes.annotationId) {
            return;
          }

          return {
            "data-annotation-id": attributes.annotationId,
            class: "annotation",
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: `span`,
        getAttrs: (node) => {
          if (node.hasAttribute("data-annotation-id") && node.classList.contains("annotation")) {
            return {
              "data-annotation-id": node.getAttribute("data-annotation-id"),
              class: "annotation",
            };
          } else {
            return false;
          }
        },
      },
    ];
  },

  renderHTML({ _mark, HTMLAttributes }) {
    const elem = document.createElement("span");

    // Merge attributes and add to element
    Object.entries(mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)).forEach(([attr, val]) => elem.setAttribute(attr, val));

    if (elem.hasAttribute("data-annotation-id")) {
      // Handlers
      const handlePointerEnter = (event) => {
        const targetAnnotationId = event.target.getAttribute("data-annotation-id").split(" ");

        this.options.onHover(targetAnnotationId);
      };

      const handlePointerLeave = () => {
        this.options.onLeave();
      };

      const handleClick = (event) => {
        // get closest annotation id
        const targetAnnotationId = event.target.closest(".annotation").getAttribute("data-annotation-id").split(" ");
        this.options.onClick(targetAnnotationId);
      };

      // Avoid memory leak with `.on...`
      elem.onpointerenter = handlePointerEnter;
      elem.onpointerleave = handlePointerLeave;
      elem.onclick = handleClick;
    }

    return elem;
  },

  addProseMirrorPlugins() {
    return [
      // Merge overlapping annotations into a single mark instead of nesting them
      new Plugin({
        key: new PluginKey("merge-annotations"),
        appendTransaction: (transactions, _oldState, newState) => {
          if (!transactions.some((tr) => tr.docChanged)) return null;

          const tr = newState.tr;

          newState.doc.descendants((node, pos) => {
            const marks = node.marks.filter((m) => m.type === this.type);

            if (marks.length > 1) {
              tr.removeMark(pos, pos + node.nodeSize, this.type);

              const allIds = marks.flatMap((m) => m.attrs.annotationId?.split(" ") ?? []);

              tr.addMark(
                pos,
                pos + node.nodeSize,
                this.type.create({
                  annotationId: [...new Set(allIds)].join(" "),
                })
              );
            }
          });

          return tr.docChanged ? tr : null;
        },
      }),
      new Plugin({
        key: new PluginKey("register-marks"),
        appendTransaction: (transactions, _oldState, newState) => {
          if (!transactions.some((tr) => tr.docChanged)) return null;
          if (!this.options.commentsManager) return null;

          // Undo/redo replays document state, not an explicit comment-row action.
          // Still update the registry, but do not delete rows when replay removes
          // a mark (including redo of History restore). Yjs marks local UndoManager
          // replays explicitly.
          const isUndoRedo = this.options.preserveCommentsOnUndoRedo && transactions.some((tr) =>
            tr.getMeta(ySyncPluginKey)?.isUndoRedoOperation
            || newState.plugins.some((plugin) => plugin.key.startsWith("history$") && tr.getMeta(plugin)),
          );
          // Cutting temporarily removes the anchor; keep its thread for paste,
          // while still updating the registry to reflect the current document.
          const isCut = transactions.some((tr) => tr.getMeta("uiEvent") === "cut");
          // Yjs projections cannot distinguish remote cuts from deletions. The
          // originating local edit owns destructive cleanup; peers only update anchors.
          const isYjsChange = transactions.some((tr) => tr.getMeta(ySyncPluginKey)?.isChangeOrigin);

          const currentIds = new Set();

          newState.doc.descendants((node, pos) => {
            const marks = node.marks.filter((m) => m.type === this.type);

            marks.forEach((mark) => {
              const ids = mark.attrs?.annotationId?.split(" ") ?? [];

              ids.forEach((id) => {
                // check if it's already registered
                currentIds.add(id);
                if (this.options.commentsManager.marks.has(id)) return;
                this.options.commentsManager.registerMark(id);
              });
            });
          });

          // delete any marks that are not in the current ids
          Array.from(this.options.commentsManager.marks.keys()).forEach((id) => {
            if (!currentIds.has(id)) {
              this.options.commentsManager.unregisterMark(id);
              if (!isUndoRedo && !isCut && !isYjsChange && typeof this.options.onMarkRemoved === "function") {
                this.options.onMarkRemoved(id);
              }
            }
          });
        },
      }),
    ];
  },
});

export default Annotation;
